import CloseIcon from '@mui/icons-material/Close';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import * as yup from 'yup';
import { toPattern } from 'vanilla-masker';
import { IBranchViewModel } from 'viewModels';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as EditPen } from '../../../assets/icons/editPen.svg';
import { MASK_CNPJ } from '../../../constants';
import { Button } from '../../Buttons/Button';
import { Input } from '../../Inputs/Input';
import { Form, Header } from './style';
import { useAlert } from '../../../hooks/useAlert';

interface ModalEditBranchProps {
  isOpen: boolean;
  runningTutorial: boolean;
  currentBranch: IBranchViewModel;
  onRequestClose: () => void;
  onRequestEditBranch: (branch: IBranchViewModel) => void;
}

const branchSchema = yup.object().shape({
  name: yup.string().required('Campo obrigatório'),
  postalCode: yup.string().required('Campo obrigatório').length(8, 'Deve ter 8 dígitos'),
  street: yup.string().required('Campo obrigatório'),
  number: yup.number().typeError('Apenas números são permitidos').required('Campo obrigatório'),
  complement: yup.string().nullable(),
});

export function ModalEditBranch({
  isOpen,
  currentBranch,
  onRequestClose,
  onRequestEditBranch,
  runningTutorial,
}: ModalEditBranchProps) {
  const alert = useAlert();

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    control,
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      name: currentBranch?.name || '',
      postalCode: currentBranch?.address?.postalCode || '',
      street: currentBranch?.address?.street || '',
      number: currentBranch?.address?.number || '',
      complement: currentBranch?.address?.complement || '',
    },
    resolver: yupResolver(branchSchema),
  });

  const handleUpdateCurrentBranch = () => {
    const updatedBranch: IBranchViewModel = {
      id: currentBranch.id,
      name: getValues('name') ? getValues('name') : currentBranch.name,
      companyId: currentBranch.companyId,
      cnpj: currentBranch.cnpj,
      address: {
        latitude: currentBranch.address.latitude,
        longitude: currentBranch.address.longitude,
        city: currentBranch.address.city,
        cityCode: currentBranch.address.cityCode,
        uf: currentBranch.address.uf,
        ufCode: currentBranch.address.ufCode,
        number: Number(getValues('number')),
        complement: getValues('complement')
          ? getValues('complement')
          : currentBranch.address.complement,
        postalCode: getValues('postalCode')
          ? getValues('postalCode')
          : currentBranch.address.postalCode,
        street: getValues('street') ? getValues('street') : currentBranch.address?.street,
      },
    };
    onRequestEditBranch(updatedBranch);
    alert.show({ message: 'Filial atualizada com sucesso!', type: 'success' });
    onRequestClose();
  };

  useEffect(() => {
    if (currentBranch && currentBranch.address) {
      setValue('name', currentBranch.name || '');
      setValue('postalCode', currentBranch.address.postalCode || '');
      setValue('street', currentBranch.address.street || '');
      setValue('number', currentBranch.address.number?.toString() || '');
      setValue('complement', currentBranch.address.complement || '');
    }
  }, [currentBranch, setValue]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-edit-modal-content">
      <Header>
        <div className="subHeader">
          <EditPen className="editPen" />
          <h1>Edição filial</h1>
        </div>
        <div className="close-div">
          <button type="button" onClick={onRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <Form>
        <div>
          <h2>CNPJ</h2>
          <p>{currentBranch?.cnpj ? toPattern(currentBranch?.cnpj, MASK_CNPJ) : ''}</p>
        </div>
        <div className="containerName">
          <Input
            name="name"
            variant="standard"
            control={control}
            label="Nome"
            lengthInput={50}
            errorText={errors.name?.message}
          />
        </div>
        <div className="containerAddress">
          <Input
            name="postalCode"
            variant="standard"
            control={control}
            label="CEP"
            className="postalCodeInput"
            lengthInput={8}
            errorText={errors.postalCode?.message}
            preventNotNumbers
          />
          <Input
            name="street"
            variant="standard"
            control={control}
            label="Endereço"
            lengthInput={50}
            className="street"
            errorText={errors.street?.message}
          />
          <Input
            name="number"
            variant="standard"
            control={control}
            label="Número"
            lengthInput={5}
            className="number"
            errorText={errors.number?.message}
            preventNotNumbers
          />
        </div>
        <div className="containerComplement">
          <Input
            name="complement"
            variant="standard"
            control={control}
            lengthInput={50}
            label="Complemento"
            className="complement"
            errorText={errors.complement?.message}
          />
        </div>
        <div className="buttonModal">
          <Button
            text=" Salvar alterações"
            dimensions={{ width: '13rem', height: '2.25rem' }}
            onClick={handleSubmit(handleUpdateCurrentBranch)}
          />
        </div>
      </Form>
    </Modal>
  );
}
